import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { faPause } from '@fortawesome/pro-light-svg-icons/faPause';
import { faPlay } from '@fortawesome/pro-light-svg-icons/faPlay';
import { faPen } from '@fortawesome/pro-light-svg-icons/faPen';
import { useNavigate } from 'react-router-dom';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useCampaignActions } from '../hooks/useCampaignActions';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import type { CampaignDetail, OutreachCampaignLive } from '../outreachTypes';
import { outreachBuilderPath } from '../outreachPaths';
import { kindsLine, monthDay } from './campaignFormat';
import { campaignPageMessages as messages } from './campaignPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the campaign page; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Campaign.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CampaignHeaderProps {
  accountId: number;
  live: OutreachCampaignLive;
  /** Undefined while `campaigns.get` loads: the line under the title waits for it. */
  campaign: CampaignDetail | undefined;
}

/**
 * The head of the campaign page (design outreach-v2 g1): "Plug is pitching", the song, the line
 * "5 a day · Spotify playlists, Blogs and magazines · from hello@… · until Dec 23", "Edit setup" (the setup of the running
 * campaign, song locked), Pause and Delete.
 */
const CampaignHeaderComponent: React.FC<CampaignHeaderProps> = ({ accountId, live, campaign }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const navigate = useNavigate();
  const plan = useOutreachPlan();
  const { pauseCampaign, resumeCampaign, deleteCampaign, isChangingState } = useCampaignActions(accountId);
  const { phase, song, campaignUuid } = live;

  const toggle = useCallback(() => {
    const run = phase === 'paused' ? resumeCampaign : pauseCampaign;
    run(campaignUuid).catch(console.error);
  }, [campaignUuid, pauseCampaign, phase, resumeCampaign]);

  const remove = useCallback(() => {
    // eslint-disable-next-line no-alert -- a short confirm before a delete
    if (window.confirm(intl.formatMessage(messages.deleteConfirm))) {
      deleteCampaign(campaignUuid)
        .then(() => router.replace(SMLRoutes.outreach))
        .catch(console.error);
    }
  }, [campaignUuid, deleteCampaign, intl, router]);

  const line = useMemo(() => {
    if (campaign === undefined) {
      return '';
    }
    const parts = [intl.formatMessage(messages.subRate, { count: campaign.dailyRate })];
    if (campaign.kinds.length > 0) {
      parts.push(kindsLine(intl, campaign.kinds));
    }
    if (live.mailboxAddress !== null) {
      parts.push(intl.formatMessage(messages.subFrom, { mailbox: live.mailboxAddress }));
    }
    if (campaign.endsAt !== null) {
      parts.push(intl.formatMessage(messages.subUntil, { date: monthDay(intl, campaign.endsAt) }));
    } else if (campaign.endWeeks === null) {
      parts.push(intl.formatMessage(messages.subUntilStopped));
    }
    return parts.join(' · ');
  }, [campaign, intl, live.mailboxAddress]);

  const eyebrow =
    phase === 'paused' ? messages.eyebrowPaused : phase === 'done' ? messages.eyebrowDone : messages.eyebrowPitching;

  return (
    <header className={styles.head} data-testid="campaign-head">
      {song.coverUrl !== null ? (
        <img className={styles.headCover} src={song.coverUrl} alt="" />
      ) : (
        <span className={styles.headCover} />
      )}
      <div className={styles.headText}>
        <span className={clsx(styles.live, phase !== 'sending' && styles.liveOff)}>
          <i className={styles.liveDot} aria-hidden />
          {intl.formatMessage(eyebrow)}
        </span>
        <h1 className={styles.headTitle}>{song.title}</h1>
        {line !== '' && (
          <p className={`${shared.sub} m-0`} data-testid="campaign-head-line">
            {line}
          </p>
        )}
      </div>
      <div className={styles.headActions}>
        {phase !== 'done' && (
          <SMLButton
            size="sm"
            outline
            omitCap
            leftIcon={faPen}
            disabled={plan.isDemo}
            // eslint-disable-next-line no-void
            onClick={() => void navigate(outreachBuilderPath({ campaign: campaignUuid, step: 'who' }))}
            testID="campaign-edit-setup"
          >
            {intl.formatMessage(messages.editSetup)}
          </SMLButton>
        )}
        {(phase === 'sending' || phase === 'paused') && (
          <SMLButton
            size="sm"
            outline
            omitCap
            leftIcon={phase === 'paused' ? faPlay : faPause}
            onClick={toggle}
            isLoading={isChangingState}
            disabled={plan.isDemo || isChangingState}
            testID="campaign-pause"
          >
            {intl.formatMessage(phase === 'paused' ? messages.resume : messages.pause)}
          </SMLButton>
        )}
        <SMLButton size="sm" color="link" omitCap disabled={plan.isDemo} onClick={remove} testID="campaign-delete">
          {intl.formatMessage(messages.delete)}
        </SMLButton>
      </div>
    </header>
  );
};

export const CampaignHeader = TypedMemo(CampaignHeaderComponent);
